import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import MatchAlertToggle from "@/components/MatchAlertToggle";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Target, Users, Trophy, Search, X, ChevronUp, ChevronDown, Plus, Gavel,
  AlertCircle, Sparkles, Wallet, Zap, Crown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const fmtMoney = (n, sym) => `${sym || ""} ${Number(n || 0).toLocaleString()}`;

// A brief audio "ping" using the Web Audio API — no assets needed.
function playPing() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch { /* noop */ }
}

function haptic() {
  try { navigator.vibrate?.([80, 40, 120, 40, 200]); } catch { /* noop */ }
}

export default function OwnerCommandCenter() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [tab, setTab] = useState("targets");
  const [targetList, setTargetList] = useState(null);
  const [competitors, setCompetitors] = useState(null);
  const [players, setPlayers] = useState([]);
  const [config, setConfig] = useState(null);
  const [search, setSearch] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const lastAlertPlayerRef = useRef(null);

  const currencySym = config?.currency_symbol || config?.currency || "";

  const loadAll = useCallback(async () => {
    try {
      const [tl, comp, pl, cfg] = await Promise.all([
        api.get("/owner/target-list"),
        api.get("/owner/competitors"),
        api.get("/players"),
        api.get("/config"),
      ]);
      setTargetList(tl.data);
      setCompetitors(comp.data);
      setPlayers(pl.data);
      setConfig(cfg.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll target-alert every 1.5s while the user is on this page.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/owner/target-alert");
        if (!alive) return;
        if (data.active && data.is_target && data.player) {
          if (lastAlertPlayerRef.current !== data.player.id) {
            lastAlertPlayerRef.current = data.player.id;
            setAlertData(data);
            setAlertOpen(true);
            playPing();
            haptic();
          } else if (alertOpen) {
            // update in-place if user is watching
            setAlertData(data);
          }
        } else if (!data.active) {
          lastAlertPlayerRef.current = null;
        }
      } catch { /* silent — auction may be idle */ }
    };
    tick();
    const iv = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(iv); };
  }, [alertOpen]);

  const targetIds = useMemo(
    () => new Set((targetList?.targets_hydrated || []).map((t) => t.player_id)),
    [targetList]
  );

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = players.filter((p) => !p.sold);
    if (q) {
      base = base.filter(
        (p) => (p.name || "").toLowerCase().includes(q)
          || (p.category || "").toLowerCase().includes(q)
          || (p.country || "").toLowerCase().includes(q)
      );
    }
    return base.slice(0, 60);
  }, [players, search]);

  const addTarget = async (player_id) => {
    try {
      const { data } = await api.post("/owner/target-list/add", { player_id });
      setTargetList(data);
      toast.success("Added to target list");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTarget = async (player_id) => {
    try {
      const { data } = await api.delete(`/owner/target-list/${player_id}`);
      setTargetList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (idx, dir) => {
    const t = [...(targetList?.targets_hydrated || [])];
    const swap = idx + dir;
    if (swap < 0 || swap >= t.length) return;
    [t[idx], t[swap]] = [t[swap], t[idx]];
    const payload = { targets: t.map((x, i) => ({ player_id: x.player_id, priority: i + 1, notes: x.notes || "" })) };
    try {
      const { data } = await api.put("/owner/target-list", payload);
      setTargetList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateNotes = async (player_id, notes) => {
    const t = (targetList?.targets_hydrated || []).map((x) => ({
      player_id: x.player_id,
      priority: x.priority,
      notes: x.player_id === player_id ? notes : (x.notes || ""),
    }));
    try {
      const { data } = await api.put("/owner/target-list", { targets: t });
      setTargetList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const myTeam = useMemo(
    () => (competitors?.teams || []).find((t) => t.is_you),
    [competitors]
  );

  if (!user) return null;
  if (!["owner", "super_admin", "league_admin"].includes(user.role)) {
    return <div className="p-8 text-zinc-400">Owner / Admin only.</div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            §3 · Franchise Owner Toolkit
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">
            Command Center
          </h1>
          <div className="text-xs text-zinc-400 mt-1">
            Top targets · competitor telemetry · real-time draft alerts.
          </div>
        </div>
        <Button onClick={() => nav("/auction")}
          className="bg-red-600 hover:bg-red-700 text-white font-bold">
          <Gavel className="w-4 h-4 mr-2" /> Live Auction
        </Button>
      </div>

      {/* My team snapshot (top of page) */}
      {myTeam && (
        <Card className="p-5 bg-gradient-to-br from-[#0d1c15] to-[#11241b] border-emerald-900/50 mb-6">
          <div className="flex flex-wrap items-center gap-6">
            <Link to={`/teams/${myTeam.id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="my-team-link">
              <Crown className="w-6 h-6 text-amber-400" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your franchise · click for full profile</div>
                <div className="text-2xl font-anton text-white">{myTeam.name}</div>
              </div>
            </Link>
            <div className="ml-auto">
              <MatchAlertToggle teamId={myTeam.id} teamName={myTeam.name} />
            </div>
            <div className="border-l border-white/10 pl-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Purse remaining</div>
              <div className="text-2xl font-mono text-emerald-400">{fmtMoney(myTeam.purse_remaining, currencySym)}</div>
              {myTeam.purse_start > 0 && (
                <Progress
                  className="mt-1 w-40 h-1.5"
                  value={Math.max(0, Math.min(100, (myTeam.purse_remaining / myTeam.purse_start) * 100))}
                />
              )}
            </div>
            <div className="border-l border-white/10 pl-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Purchases</div>
              <div className="text-2xl font-mono text-white">{myTeam.purchases}</div>
            </div>
            <div className="border-l border-white/10 pl-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total spent</div>
              <div className="text-2xl font-mono text-white">{fmtMoney(myTeam.total_spent, currencySym)}</div>
            </div>
            <div className="border-l border-white/10 pl-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Roster</div>
              <div className="text-2xl font-mono text-white">
                {myTeam.roster.count} / {config?.max_players_per_team || 7}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#11241b] border border-white/10">
          <TabsTrigger value="targets" data-testid="occ-tab-targets"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Target className="w-4 h-4 mr-1.5" /> Top Targets
            {targetList?.targets_hydrated?.length ? (
              <Badge className="ml-2 bg-red-600 text-white h-5 px-1.5 text-[10px]">{targetList.targets_hydrated.length}</Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="competitors" data-testid="occ-tab-competitors"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-1.5" /> Competitors
          </TabsTrigger>
          <TabsTrigger value="roster" data-testid="occ-tab-roster"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Trophy className="w-4 h-4 mr-1.5" /> My Roster
          </TabsTrigger>
        </TabsList>

        {/* Top Targets ---------------------------------------------------- */}
        <TabsContent value="targets" className="mt-4 space-y-4">
          <Card className="p-4 bg-[#11241b] border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <h3 className="font-anton text-lg uppercase text-white">Your priority list</h3>
              <span className="text-xs text-zinc-500">
                Fires an instant alert the moment any of these players becomes the auction&apos;s current lot.
              </span>
            </div>
            {targetList?.targets_hydrated?.length ? (
              <div className="space-y-2">
                {targetList.targets_hydrated.map((t, idx) => (
                  <div key={t.player_id}
                    data-testid={`target-row-${t.player_id}`}
                    className={`flex items-center gap-3 p-3 rounded-md border ${t.is_sold ? "opacity-50 border-red-900/40 bg-red-950/20" : "border-white/5 bg-[#0d1c15]"}`}>
                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center font-black text-white shrink-0">
                      #{t.priority}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{t.player?.name}</span>
                        <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                          Cat {t.player?.category}
                        </Badge>
                        {t.player?.is_international && (
                          <Badge variant="outline" className="border-amber-700 text-amber-500 text-[10px]">INTL</Badge>
                        )}
                        {t.is_sold && <Badge className="bg-red-600 text-white text-[10px]">SOLD</Badge>}
                        <span className="text-xs text-zinc-500">
                          Rating {t.player?.rating} · Seed {t.player?.seed_level}
                        </span>
                      </div>
                      <Textarea
                        placeholder="Notes (why is this player a priority?)"
                        value={t.notes || ""}
                        onChange={(e) => {
                          setTargetList((prev) => ({
                            ...prev,
                            targets_hydrated: prev.targets_hydrated.map((x, i) =>
                              i === idx ? { ...x, notes: e.target.value } : x
                            ),
                          }));
                        }}
                        onBlur={(e) => updateNotes(t.player_id, e.target.value)}
                        className="mt-1 bg-[#060d09] border-white/5 text-white text-xs h-14"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="icon" variant="outline"
                        className="border-white/10 text-zinc-300 h-7 w-7"
                        onClick={() => move(idx, -1)} disabled={idx === 0}>
                        <ChevronUp className="w-3 h-3" />
                      </Button>
                      <Button size="icon" variant="outline"
                        className="border-white/10 text-zinc-300 h-7 w-7"
                        onClick={() => move(idx, +1)} disabled={idx === targetList.targets_hydrated.length - 1}>
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </div>
                    <Button size="icon" variant="outline"
                      className="border-red-900/50 text-red-400 hover:bg-red-950 h-8 w-8"
                      onClick={() => removeTarget(t.player_id)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-6 text-zinc-500">
                No targets yet. Browse players below and hit <span className="text-emerald-400">+ Add</span> to build your scouting list.
              </div>
            )}
          </Card>

          {/* Player pool */}
          <Card className="p-4 bg-[#11241b] border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search available players by name / category / country"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#060d09] border-white/10 text-white"
              />
              <span className="text-xs text-zinc-500 whitespace-nowrap">{searchResults.length} shown</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[520px] overflow-y-auto">
              {searchResults.map((p) => {
                const already = targetIds.has(p.id);
                return (
                  <div key={p.id}
                    className={`p-3 rounded-md border ${already ? "border-emerald-800 bg-emerald-950/20" : "border-white/5 bg-[#0d1c15]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">{p.name}</div>
                        <div className="flex items-center gap-1 flex-wrap mt-1">
                          <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] px-1">
                            {p.category || "?"}
                          </Badge>
                          {p.is_international && (
                            <Badge variant="outline" className="border-amber-700 text-amber-500 text-[10px] px-1">INTL</Badge>
                          )}
                          <span className="text-[10px] text-zinc-500">R {p.rating} · S {p.seed_level}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={already ? "outline" : "default"}
                        className={already
                          ? "border-emerald-800 text-emerald-400 h-7 px-2 text-xs"
                          : "bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"}
                        onClick={() => already ? removeTarget(p.id) : addTarget(p.id)}
                        data-testid={`pool-add-${p.id}`}
                      >
                        {already ? <><X className="w-3 h-3 mr-1" /> On list</> : <><Plus className="w-3 h-3 mr-1" /> Add</>}
                      </Button>
                    </div>
                  </div>
                );
              })}
              {searchResults.length === 0 && (
                <div className="col-span-full text-center p-8 text-zinc-500 text-sm">
                  No players match. Adjust search or clear filters.
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Competitors ---------------------------------------------------- */}
        <TabsContent value="competitors" className="mt-4 space-y-4">
          {!competitors?.teams?.length && (
            <Card className="p-8 text-center text-zinc-500 bg-[#11241b] border-white/10">
              No competitor franchises registered yet.
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(competitors?.teams || []).filter((t) => !t.is_you).map((t) => {
              const purseUsed = t.purse_start ? Math.max(0, Math.min(100, ((t.purse_start - t.purse_remaining) / t.purse_start) * 100)) : 0;
              return (
                <Card key={t.id} className="p-4 bg-[#11241b] border-white/10 space-y-3 hover:border-[#F4B400]/50 transition-colors"
                  data-testid={`competitor-${t.id}`}>
                  <Link to={`/teams/${t.id}`} className="flex items-center gap-3 group" data-testid={`competitor-link-${t.id}`}>
                    <div className="w-10 h-10 rounded flex items-center justify-center text-white font-black shrink-0 overflow-hidden"
                      style={{ background: t.brand_color || "#111" }}>
                      {t.logo_url ? <img src={t.logo_url} alt={t.name} className="w-full h-full object-contain" /> : (t.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate group-hover:text-[#F4B400] transition-colors">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {t.owner_names?.length ? t.owner_names.join(" · ") : "no owners assigned"}
                      </div>
                    </div>
                    <Badge variant="outline" className="border-red-800 text-red-400 text-[10px]">RIVAL</Badge>
                  </Link>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 rounded bg-[#0d1c15] border border-white/5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">Purse left</div>
                      <div className="font-mono text-emerald-400">{fmtMoney(t.purse_remaining, currencySym)}</div>
                    </div>
                    <div className="p-2 rounded bg-[#0d1c15] border border-white/5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">Purchases</div>
                      <div className="font-mono text-white">{t.purchases}</div>
                    </div>
                    <div className="p-2 rounded bg-[#0d1c15] border border-white/5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">Spent</div>
                      <div className="font-mono text-white">{fmtMoney(t.total_spent, currencySym)}</div>
                    </div>
                  </div>
                  <Progress value={purseUsed} className="h-1.5" />

                  {t.recent_purchases?.length ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Recent buys</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {t.recent_purchases.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-zinc-300 truncate">{r.player_name || r.player_id}</span>
                            <span className="font-mono text-amber-400">{fmtMoney(r.amount, currencySym)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-600 italic">no purchases yet</div>
                  )}

                  {/* Roster composition */}
                  <div className="border-t border-white/5 pt-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Roster ({t.roster.count})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(t.roster.by_category || {}).map(([c, n]) => (
                        <Badge key={c} variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                          {c}: {n}
                        </Badge>
                      ))}
                      {t.roster.international_count > 0 && (
                        <Badge variant="outline" className="border-amber-700 text-amber-500 text-[10px]">
                          INTL: {t.roster.international_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* My Roster ------------------------------------------------------- */}
        <TabsContent value="roster" className="mt-4 space-y-4">
          {myTeam ? (
            <Card className="p-4 bg-[#11241b] border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-400" />
                <h3 className="font-anton text-lg uppercase text-white">{myTeam.name} · roster</h3>
              </div>
              {myTeam.roster.count === 0 ? (
                <div className="text-center p-6 text-zinc-500">
                  No players yet — head to the <span className="text-emerald-400">Live Auction</span> to build your squad.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(myTeam.recent_purchases || []).map((r, i) => (
                    <div key={i} className="p-3 rounded-md border border-white/5 bg-[#0d1c15]">
                      <div className="text-sm text-white truncate">{r.player_name}</div>
                      <div className="font-mono text-amber-400 text-xs mt-1">{fmtMoney(r.amount, currencySym)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-8 text-center text-zinc-500 bg-[#11241b] border-white/10">
              You aren&apos;t assigned to a franchise yet. Ask the League Admin.
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Target announcement modal --------------------------------------- */}
      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="bg-gradient-to-br from-red-900/40 via-black to-emerald-900/40 border-red-800 max-w-lg text-white">
          <DialogHeader>
            <DialogTitle className="text-3xl font-anton uppercase tracking-tight flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-400 animate-pulse" />
              Target Player Announced
            </DialogTitle>
            <DialogDescription className="text-red-200">
              One of your priority signings is on the block right now.
            </DialogDescription>
          </DialogHeader>

          {alertData?.player && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {alertData.player.photo_url ? (
                  <img
                    src={alertData.player.photo_url}
                    alt=""
                    className="w-20 h-20 rounded-full object-cover border-2 border-amber-400"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-emerald-600 flex items-center justify-center text-2xl font-black text-white">
                    {alertData.player.name?.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="text-2xl font-anton uppercase leading-tight">{alertData.player.name}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className="bg-red-600 text-white">#{alertData.priority} of {alertData.of_total}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">Cat {alertData.player.category}</Badge>
                    {alertData.player.is_international && (
                      <Badge variant="outline" className="border-amber-500 text-amber-400">International</Badge>
                    )}
                    <span className="text-xs text-red-100/60">Rating {alertData.player.rating}</span>
                  </div>
                </div>
              </div>

              {alertData.notes && (
                <div className="p-3 rounded-md bg-black/40 border border-amber-900/40">
                  <div className="text-[10px] uppercase tracking-widest text-amber-500 mb-1">Your notes</div>
                  <div className="text-sm text-amber-100 italic">&ldquo;{alertData.notes}&rdquo;</div>
                </div>
              )}

              {alertData.fit && (
                <div className="p-3 rounded-md bg-black/40 border border-emerald-900/40 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-500 mb-1">
                    Roster fit analysis
                  </div>
                  {alertData.fit.gaps.map((g, i) => (
                    <div key={i} className="text-xs text-emerald-100 flex items-start gap-2">
                      <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{g}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-white/5 text-xs">
                    <div>
                      <span className="text-zinc-500">Purse </span>
                      <span className="font-mono text-emerald-400">{fmtMoney(alertData.fit.purse_remaining, currencySym)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Roster </span>
                      <span className="font-mono text-white">{alertData.fit.roster_count}/{alertData.fit.roster_max}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-3 rounded-md bg-black/40 border border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Current bid</div>
                  <div className="font-mono text-lg text-amber-300">
                    {fmtMoney(alertData.current_bid, currencySym)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Starting bid</div>
                  <div className="font-mono text-lg text-white">
                    {fmtMoney(alertData.starting_bid, currencySym)}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white"
                  onClick={() => setAlertOpen(false)}
                  data-testid="alert-skip">
                  Skip
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  onClick={() => { setAlertOpen(false); nav("/auction"); }}
                  data-testid="alert-bid">
                  <Wallet className="w-4 h-4 mr-1.5" /> Bid Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
